/**
 * Digest Send Pipeline — the SINGLE canonical governed send path for the
 * customer-facing weekly digest.
 *
 * RECONCILIATION: several email paths existed before this file —
 *   • src/lib/email.ts ................ Firestore `outbox` transactional send
 *   • src/lib/agent/emailLog.ts ....... Gmail *read* sync (not a send path)
 *   • src/lib/crm/customerEmailPipeline.ts approval-item assembly (no send)
 * Those remain read-only references. Every weekly-digest send goes through
 * {@link approveAndSend} and NOTHING else: the UI's Send button calls it
 * with injected deps (approval gate, email log, ledger), so a bypass is a
 * type error and an unapproved call is a thrown error — governance is
 * structural, not advisory.
 *
 * The loop: draft (digestComposer) → {@link toApprovableAction} (an
 * ActionDiff-shaped proposal with a visual diff vs the prior send and the
 * honest reversibility class) → {@link approveAndSend} (approval REQUIRED,
 * then one email-log append + one fnvHash ledger seal) →
 * {@link recallDigest} (the governed compensating recall — a sent email
 * cannot be unsent, and the record says so in the reversal engine's words).
 *
 * PURE by contract: no store, no firebase, no network — all IO arrives
 * through the injected deps, so tests run offline and the UI wires the
 * real seams (src/lib/email.ts outbox, the Decision Ledger).
 */

import type { Account } from '../db/types';
import { fnvHash } from '../ledger/verifyChain';
import { IRREVERSIBLE_REASONS, REVERSAL_NOTE_PREFIX } from '../agent/reversal';
import { classifyReversibility, type Reversibility } from '../agent/reversibility';
import { resolveWeeklyUpdateRecipient } from './weeklyUpdate';
import type { DigestDraft, DigestProvenance } from './digestComposer';

// ---------------------------------------------------------------------------
// toApprovableAction — the ActionDiff-shaped proposal
// ---------------------------------------------------------------------------

/** Visual old→new diff vs the prior send (actionDiff discipline). */
export interface DigestDiff {
  changed: boolean;
  /** 'subject: A → B', '+ added line', '− removed line'. */
  lines: string[];
  summary: string;
}

/** The prior send to diff against — subject/body is all a diff needs. */
export interface PriorDigest {
  subject: string;
  body: string;
}

/** An approvable send proposal — never a write by itself. */
export interface DigestSendAction {
  /** Deterministic id — the same draft builds the same proposal. */
  id: string;
  kind: 'send_digest_email';
  accountId: string;
  accountName: string;
  /** Resolved recipient email — null means "cannot send", never a guess. */
  to: string | null;
  recipientName: string | null;
  subject: string;
  body: string;
  /** Visual diff vs the prior send, for the approval card. */
  diff: DigestDiff;
  /** Honest reversibility class + the reversal engine's exact wording. */
  reversibility: Reversibility;
  reversibilityReason: string;
  provenance: DigestProvenance;
  status: 'pending';
}

function diffVsPrior(subject: string, body: string, prior?: PriorDigest): DigestDiff {
  if (!prior) {
    return { changed: true, lines: [], summary: 'First send — no prior digest to diff.' };
  }
  const lines: string[] = [];
  if (prior.subject !== subject) lines.push(`subject: ${prior.subject} → ${subject}`);
  const prevLines = prior.body.split('\n').map((l) => l.trim()).filter(Boolean);
  const nextLines = body.split('\n').map((l) => l.trim()).filter(Boolean);
  const prevSet = new Set(prevLines);
  const nextSet = new Set(nextLines);
  for (const l of prevLines) if (!nextSet.has(l)) lines.push(`− ${l}`);
  for (const l of nextLines) if (!prevSet.has(l)) lines.push(`+ ${l}`);
  const changed = lines.length > 0;
  return {
    changed,
    lines,
    summary: changed
      ? `${lines.length} change${lines.length === 1 ? '' : 's'} vs the last sent digest`
      : 'No changes vs the last sent digest.',
  };
}

/**
 * Compile a draft into the approvable proposal the queue reviews: the
 * resolved recipient (via the canonical `resolveWeeklyUpdateRecipient` —
 * no address is ever guessed), the visual diff vs the prior send, and the
 * honest reversibility metadata. PURE: nothing is queued or sent here.
 */
export function toApprovableAction(
  draft: DigestDraft,
  account: Account,
  prior?: PriorDigest,
): DigestSendAction {
  const recipient = resolveWeeklyUpdateRecipient(account);
  const digest = fnvHash('', `${account.id}\u0000${draft.subject}\u0000${draft.body}`);
  return {
    id: `digest-send-${account.id}-${digest.replace(/^fnv1a:/, '')}`,
    kind: 'send_digest_email',
    accountId: account.id,
    accountName: account.name,
    to: recipient?.email ?? null,
    recipientName: recipient?.name ?? null,
    subject: draft.subject,
    body: draft.body,
    diff: diffVsPrior(draft.subject, draft.body, prior),
    reversibility: classifyReversibility('send_email'),
    reversibilityReason:
      IRREVERSIBLE_REASONS.send_email ??
      'This email has already been sent — it left the system and cannot be unsent.',
    provenance: draft.provenance,
    status: 'pending',
  };
}

// ---------------------------------------------------------------------------
// approveAndSend — the ONE canonical send path
// ---------------------------------------------------------------------------

/** The approval gate. `approve` returning `approved: false` blocks the send. */
export interface DigestApprovalGate {
  approve(
    action: DigestSendAction,
  ): Promise<{ approved: boolean; decidedBy?: string }> | { approved: boolean; decidedBy?: string };
}

/** One sealed entry in the digest email log. */
export interface SentDigestEntry {
  sendId: string;
  accountId: string;
  to: string;
  subject: string;
  body: string;
  sentAt: string;
  approvedBy: string;
}

/** Append-only email log seam — the UI wires the real outbox send here. */
export interface DigestEmailLog {
  append(entry: SentDigestEntry): Promise<unknown> | unknown;
}

/** The Decision-Ledger seam: seals the send with its fnvHash. */
export interface DigestLedger {
  seal(entry: {
    sendId: string;
    accountId: string;
    actionType: 'send_email';
    hash: string;
    at: string;
  }): Promise<unknown> | unknown;
}

export interface DigestSendDeps {
  approvalQueue: DigestApprovalGate;
  emailLog: DigestEmailLog;
  ledger: DigestLedger;
}

/** The sealed result a completed governed send returns. */
export interface SealedSendResult {
  sendId: string;
  accountId: string;
  to: string;
  subject: string;
  sentAt: string;
  /** fnvHash over the exact sent payload — the ledger seal. */
  ledgerHash: string;
  approvedBy: string;
}

/**
 * The SINGLE canonical send: approval FIRST (an unapproved or rejected
 * action throws before any IO), then exactly one email-log append and one
 * ledger seal. There is no other exported function that can emit a digest
 * send — bypass is structurally impossible, not merely discouraged.
 *
 * Throws when:
 *   • the action is not a pending `send_digest_email` proposal;
 *   • no recipient email resolved ("No contact email on file");
 *   • the approval gate does not return `approved: true`.
 */
export async function approveAndSend(
  action: DigestSendAction,
  deps: DigestSendDeps,
): Promise<SealedSendResult> {
  if (action.kind !== 'send_digest_email' || action.status !== 'pending') {
    throw new Error('approveAndSend requires a pending send_digest_email proposal.');
  }
  if (!action.to) {
    throw new Error(`Cannot send digest for ${action.accountName}: no contact email on file.`);
  }

  // Approval gate FIRST — nothing sends without an explicit approval.
  const decision = await deps.approvalQueue.approve(action);
  if (!decision || decision.approved !== true) {
    throw new Error(
      `Digest send for ${action.accountName} was not approved — nothing was sent.`,
    );
  }

  const sentAt = new Date().toISOString();
  const sendId = `send-${action.id}`;
  const approvedBy = decision.decidedBy ?? 'operator';

  const entry: SentDigestEntry = {
    sendId,
    accountId: action.accountId,
    to: action.to,
    subject: action.subject,
    body: action.body,
    sentAt,
    approvedBy,
  };
  await deps.emailLog.append(entry);

  const ledgerHash = fnvHash(
    '',
    `${sendId}\u0000${action.accountId}\u0000${action.to}\u0000${action.subject}\u0000${action.body}\u0000${sentAt}`,
  );
  await deps.ledger.seal({
    sendId,
    accountId: action.accountId,
    actionType: 'send_email',
    hash: ledgerHash,
    at: sentAt,
  });

  return {
    sendId,
    accountId: action.accountId,
    to: action.to,
    subject: action.subject,
    sentAt,
    ledgerHash,
    approvedBy,
  };
}

// ---------------------------------------------------------------------------
// recallDigest — the honest reversal
// ---------------------------------------------------------------------------

/** The governed recall record a reversal seam receives. */
export interface DigestRecallRecord {
  /** Deterministic — recalling the same send twice compiles the same record. */
  id: string;
  kind: 'digest_recall';
  /** The sealed send this recall compensates. */
  reversesSendId: string;
  /**
   * 'compensating': a sent email left the building and cannot be unsent —
   * the recall is a governed correction, never a pretended deletion.
   */
  mode: 'compensating';
  /** Reversal-engine-parseable note (REVERSAL_NOTE_PREFIX discipline). */
  note: string;
  at: string;
}

/** The injected reversal seam — the UI wires the real reversal ledger here. */
export interface DigestReversalDeps {
  reversal: { record(rec: DigestRecallRecord): unknown };
}

/**
 * Compile + record the compensating recall for a sealed digest send. Honest
 * by construction: the note carries the reversal engine's exact "cannot be
 * unsent" wording, so nobody mistakes a recall for an unsend. Returns the
 * record so the UI can show the reversal chip immediately.
 */
export function recallDigest(sendId: string, deps: DigestReversalDeps): DigestRecallRecord {
  const reason =
    IRREVERSIBLE_REASONS.send_email ??
    'This email has already been sent — it left the system and cannot be unsent.';
  const record: DigestRecallRecord = {
    id: `recall-${sendId}`,
    kind: 'digest_recall',
    reversesSendId: sendId,
    mode: 'compensating',
    note: `${REVERSAL_NOTE_PREFIX}${sendId} — ${reason} A correction follow-up is the compensating action.`,
    at: new Date().toISOString(),
  };
  deps.reversal.record(record);
  return record;
}
